import React, { useRef } from "react";
import { Box } from "@mui/material";
import { useDrag, useDrop } from "react-dnd";
import MenuActions from "./MenuActions";

const ItemTypes = {
  MENU_ITEM: "menuItem",
};

const MenuItem = ({
  item,
  itemIndex,
  level,
  levelIndex,
  isExpanded,
  isVisible,
  currentIndices,
  onItemClick,
  onEdit,
  onVisibilityToggle,
  onColorEdit,
  onDelete,
  onPositionEdit,
  onMove,
  sx,
}) => {
  const ref = useRef(null);

  console.log(sx, `=============`);

  // Set up drag functionality
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.MENU_ITEM,
    item: () => ({
      id: item.id,
      index: itemIndex,
      level,
      levelIndex,
    }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Set up drop functionality
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.MENU_ITEM,
    hover: (draggedItem, monitor) => {
      if (!ref.current) return;

      const dragIndex = draggedItem.index;
      const hoverIndex = itemIndex;
      const dragLevel = draggedItem.level;
      const dragLevelIndex = draggedItem.levelIndex;

      // Don't replace items with themselves
      if (
        dragIndex === hoverIndex &&
        dragLevel === level &&
        dragLevelIndex === levelIndex
      ) {
        return;
      }

      // Get rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Get mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      // Time to actually perform the action
      onMove(draggedItem, {
        index: hoverIndex,
        level,
        levelIndex,
      });

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      draggedItem.index = hoverIndex;
      draggedItem.level = level;
      draggedItem.levelIndex = levelIndex;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  // Initialize drag and drop refs
  drag(drop(ref));

  const getItemStyles = () => {
    // Extract color values with defaults
    const {
      colors: {
        activeButton = level > 0 ? "#d4d9de" : "#fff",
        defaultButton = level > 0 ? "#e3e3e336" : "#fff",
        defaultText = "#000",
        activeText = "#000",
        hoverButton = "#d4d9de",
        hoverText = "#000",
        defaultBorder = "#e0e0e0",
        activeBorder = "#000",
        hoverBorder = "#e0e0e0",
      } = {},
    } = item || {};

    const getBorderStyle = () => {
      if (level > 0) return `1px solid ${defaultBorder}`;
      return isExpanded
        ? `1px solid ${activeBorder} `
        : `1px solid ${defaultBorder}`;
    };

    const getBackgroundColor = () => {
      if (isDragging) return "rgba(212, 217, 222, 0.5)";
      if (isOver) return "rgba(212, 217, 222, 0.3)";
      return isExpanded ? activeButton : defaultButton;
    };

    const getHeight = () => {
      if (level >= 3) return "6.5rem";
      if (levelIndex === 2 && itemIndex > 0 && itemIndex < 4) return "104px";
      return "40px";
    };

    const baseStyles = {
      display: "flex",
      alignItems: "center",
      columnGap: "7px",
      cursor: "move",
      opacity: isDragging ? 0.5 : isVisible ? 1 : 0.5,
      color: isExpanded ? activeText : defaultText,
      transition: "background-color 0.2s, opacity 0.2s",
      "&:hover": {
        backgroundColor: hoverButton,
        color: hoverText,
        border: level === 0 && `1px solid ${hoverBorder}`,
      },
    };

    const levelStyles = {
      padding: level > 0 ? "24px 8px" : "4px 8px",
      borderRadius: level > 0 ? "5px" : "50px",
      width: level > 0 ? "100%" : "auto",
    };

    return {
      ...baseStyles,
      ...levelStyles,
      border: getBorderStyle(),
      backgroundColor: getBackgroundColor(),
      height: getHeight(),
    };
  };

  const renderItemContent = () => {
    if (level === 2 && levelIndex === 1) {
      return (
        <a
          href={item.link}
          style={{
            color: "inherit",
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {item.image ? (
            <img
              src={item.image}
              alt="menu item"
              style={{
                height: "40px",
                width: "40px",
                objectFit: "contain",
              }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "5px 8px",
                borderRadius: "5px",
                border: "1px solid #e0e0e0",
                backgroundColor: "#fff",
                width: "40px",
                height: "40px",
                marginRight: "5px",
                fontSize: "14px",
                color: "#000",
              }}
            >
              Img
            </Box>
          )}
          <Box>
            <span
              style={{
                fontSize: "14px",
                color: "inherit",
                fontWeight: item.isBold ? "bold" : "",
              }}
            >
              {item.title}
            </span>
          </Box>
        </a>
      );
    }

    if (level === 2 && levelIndex === 2 && itemIndex > 0 && itemIndex < 4) {
      return (
        <>
          {item.image ? (
            <img
              src={item.image}
              alt="menu item"
              style={{
                height: "100px",
                width: "100px",
                objectFit: "contain",
              }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "5px 8px",
                borderRadius: "5px",
                border: "1px solid #e0e0e0",
                backgroundColor: "#fff",
                width: "100px",
                height: "100px",
                marginRight: "5px",
              }}
            />
          )}
          <Box>
            <span
              style={{
                fontSize: "14px",
                color: item.colors ? item.colors.defaultTitle : "inherit",
                fontWeight: item.isBold ? "bold" : "",
              }}
            >
              {item.title}
            </span>
            <br />
            <span
              style={{
                fontSize: "11px",
                color: item.colors ? item.colors.defaultBody : "inherit",
              }}
            >
              {item.body}
            </span>
          </Box>
        </>
      );
    }

    return (
      <Box>
        <span
          style={{
            fontSize: "14px",
            color: item.colors ? item.colors.defaultTitle : "inherit",
            fontWeight: item.isBold ? "bold" : "",
          }}
        >
          {item.title}
        </span>
        <br />
        <span style={{ fontSize: "11px", color: "grey" }}>{item.body}</span>
      </Box>
    );
  };

  return (
    <Box
      ref={ref}
      onClick={(e) => {
        e.stopPropagation();
        onItemClick();
      }}
      sx={{
        ...getItemStyles(),
        ...sx,
      }}
    >
      {renderItemContent()}
      <MenuActions
        level={level}
        itemIndex={itemIndex}
        levelIndex={levelIndex}
        isVisible={isVisible}
        currentIndices={currentIndices}
        onEdit={onEdit}
        onVisibilityToggle={onVisibilityToggle}
        onColorEdit={onColorEdit}
        onDelete={onDelete}
        onPositionEdit={onPositionEdit}
        position={level > 0 && levelIndex < 2 ? item.position : null}
      />
    </Box>
  );
};

export default MenuItem;
